import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Boxes,
  LayoutDashboard,
  Rocket,
  Code2,
  RefreshCw,
  Database,
  Image as ImageIcon,
  LifeBuoy,
  ArrowLeft,
  Sun,
  Moon,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Menu,
  X,
  Workflow,
  Building2,
  KeyRound,
  Shield,
  Gauge,
  Server,
  CreditCard,
  Filter,
  Layers,
  Sparkles,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import CodeBlock from "./CodeBlock";
import { CANONICAL_WRAPPER } from "./canonicalWrapper";

/* ──────────────────────────────────────────────────────────────
 *  Zichtbaar Doc
 *  Publieke documentatie (geen login nodig) — /docs
 *  Voor admin-collega's die een klantensite koppelen aan de
 *  Zichtbaar-backend via de gehoste SDK.
 *
 *  Opgebouwd in twee sporen:
 *   • "Snel koppelen"        — van leeg mapje tot live data
 *   • "Hoe het echt werkt"   — wat er onder de motorkap gebeurt
 *  Elke sectie is getagd met de LAGEN waarin ze speelt. Bovenaan
 *  kun je op een laag filteren.
 * ────────────────────────────────────────────────────────────── */

/* ── Lagen (de enige tag-dimensie) ───────────────────────────── */

const LAYERS = {
  frontend: {
    label: "Frontend",
    dot: "bg-sky-500",
    light: "bg-sky-50 text-sky-700 border-sky-200",
    dark: "bg-sky-500/10 text-sky-300 border-sky-500/25",
  },
  sdk: {
    label: "SDK",
    dot: "bg-blue-500",
    light: "bg-blue-50 text-blue-700 border-blue-200",
    dark: "bg-blue-500/10 text-blue-300 border-blue-500/25",
  },
  backend: {
    label: "Backend-API",
    dot: "bg-violet-500",
    light: "bg-violet-50 text-violet-700 border-violet-200",
    dark: "bg-violet-500/10 text-violet-300 border-violet-500/25",
  },
  database: {
    label: "Database",
    dot: "bg-emerald-500",
    light: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dark: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
  },
  infra: {
    label: "Infra · AWS/CORS",
    dot: "bg-amber-500",
    light: "bg-amber-50 text-amber-700 border-amber-200",
    dark: "bg-amber-500/10 text-amber-300 border-amber-500/25",
  },
  payment: {
    label: "Betaling · Mollie",
    dot: "bg-pink-500",
    light: "bg-pink-50 text-pink-700 border-pink-200",
    dark: "bg-pink-500/10 text-pink-300 border-pink-500/25",
  },
};

const LAYER_ORDER = ["frontend", "sdk", "backend", "database", "infra", "payment"];

/* ── Sporen + secties ────────────────────────────────────────── */

const TRACKS = [
  { id: "connect", label: "Snel koppelen", hint: "Van leeg mapje tot live data" },
  { id: "deep", label: "Hoe het echt werkt", hint: "Wat er onder de motorkap gebeurt" },
  { id: "roadmap", label: "Roadmap", hint: "Wat we nog willen verbeteren" },
];

const SECTIONS = [
  // Spoor 1 — Snel koppelen
  { id: "intro", label: "Introductie", icon: BookOpen, track: "connect", layers: [] },
  { id: "architectuur", label: "Hoe het samenhangt", icon: Boxes, track: "connect", layers: ["frontend", "sdk", "backend"] },
  { id: "dashboard", label: "Het dashboard gebruiken", icon: LayoutDashboard, track: "connect", layers: ["backend"] },
  { id: "quickstart", label: "Nieuw project starten", icon: Rocket, track: "connect", layers: ["frontend", "sdk"] },
  { id: "wrapper", label: "De API-wrapper", icon: Code2, track: "connect", layers: ["sdk", "frontend"] },
  { id: "reference", label: "API-referentie", icon: Database, track: "connect", layers: ["sdk", "backend"] },
  { id: "data", label: "Data & afbeeldingen", icon: ImageIcon, track: "connect", layers: ["sdk", "infra"] },
  // Spoor 2 — Hoe het echt werkt
  { id: "lifecycle", label: "Levensloop van een verzoek", icon: Workflow, track: "deep", layers: ["frontend", "sdk", "backend", "database", "infra"] },
  { id: "multitenancy", label: "Multi-tenant: één backend", icon: Building2, track: "deep", layers: ["backend", "database"] },
  { id: "auth", label: "Auth & rechten", icon: KeyRound, track: "deep", layers: ["backend"] },
  { id: "cors", label: "De twee CORS-lagen", icon: Shield, track: "deep", layers: ["infra", "backend"] },
  { id: "ratelimit", label: "Rate-limiting & betrouwbaarheid", icon: Gauge, track: "deep", layers: ["infra", "backend"] },
  { id: "media", label: "S3 & media onder de motorkap", icon: Server, track: "deep", layers: ["infra"] },
  { id: "payments", label: "Mollie-betalingen", icon: CreditCard, track: "deep", layers: ["payment", "backend"] },
  { id: "caching", label: "SDK-caching & updates", icon: RefreshCw, track: "deep", layers: ["sdk", "infra"] },
  { id: "troubleshooting", label: "Problemen oplossen", icon: LifeBuoy, track: "deep", layers: ["frontend", "sdk", "backend", "database", "infra", "payment"] },
  // Spoor 3 — Roadmap
  { id: "roadmap", label: "Wat we nog willen verbeteren", icon: Sparkles, track: "roadmap", layers: ["frontend", "sdk", "backend", "database", "infra", "payment"] },
];

const SDK_URL = "https://api.zichtbaarstudio.be/sdk.js";
const API_URL = "https://api.zichtbaarstudio.be/api";

export default function ZichtbaarDoc() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const [active, setActive] = useState("intro");
  const [navOpen, setNavOpen] = useState(false);
  const [activeLayers, setActiveLayers] = useState(() => new Set());
  const observer = useRef(null);

  const filterKey = [...activeLayers].sort().join(",");

  const matches = (layers) =>
    activeLayers.size === 0 || layers.some((l) => activeLayers.has(l));

  const toggleLayer = (layer) =>
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layer)) next.delete(layer);
      else next.add(layer);
      return next;
    });

  const clearLayers = () => setActiveLayers(new Set());

  // Scrollspy — markeer de zichtbare sectie in de zijbalk.
  // Herloopt zodra het filter verandert (verborgen secties verdwijnen uit de DOM).
  useEffect(() => {
    observer.current = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.current.observe(el);
    });
    return () => observer.current?.disconnect();
  }, [filterKey]);

  const scrollTo = (id) => {
    setNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const text = isDark ? "text-gray-300" : "text-gray-700";
  const muted = isDark ? "text-gray-400" : "text-gray-600";
  const heading = isDark ? "text-white" : "text-gray-900";

  const sharedProps = { isDark, text, muted, heading, activeLayers, onTag: toggleLayer };

  return (
    <div className={isDark ? "min-h-screen bg-[#0a0a0a]" : "min-h-screen bg-[#fafafa]"}>
      {/* Topbar */}
      <header
        className={`sticky top-0 z-30 flex items-center justify-between border-b px-4 py-3 backdrop-blur-md sm:px-6 ${
          isDark ? "border-white/[0.06] bg-[#0a0a0a]/80" : "border-gray-200 bg-white/80"
        }`}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setNavOpen((v) => !v)}
            className={`rounded-md p-1.5 lg:hidden ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
            aria-label="Menu"
          >
            {navOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div className="flex items-center gap-2">
            <div className={`rounded-lg p-1.5 ${isDark ? "bg-blue-500/15" : "bg-blue-50"}`}>
              <BookOpen size={16} className={isDark ? "text-blue-400" : "text-blue-600"} />
            </div>
            <span className={`text-[15px] font-bold tracking-tight ${heading}`}>
              Zichtbaar Doc
            </span>
            <span
              className={`hidden rounded-full px-2 py-0.5 text-[10px] font-medium sm:inline-block ${
                isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-500"
              }`}
            >
              v2
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={toggleTheme}
            className={`rounded-md p-2 ${isDark ? "text-gray-400 hover:bg-white/10" : "text-gray-500 hover:bg-gray-100"}`}
            aria-label="Thema wisselen"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <Link
            to="/dashboard"
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12.5px] font-medium ${
              isDark ? "text-gray-300 hover:bg-white/10" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <ArrowLeft size={14} /> Dashboard
          </Link>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1180px]">
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-20 w-64 transform overflow-y-auto border-r px-3 pb-10 pt-20 transition-transform lg:sticky lg:top-[57px] lg:h-[calc(100vh-57px)] lg:translate-x-0 lg:pt-6 ${
            navOpen ? "translate-x-0" : "-translate-x-full"
          } ${isDark ? "border-white/[0.06] bg-[#0a0a0a]" : "border-gray-200 bg-[#fafafa]"}`}
        >
          {TRACKS.map((track) => (
            <div key={track.id} className="mb-5">
              <p className={`mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider ${muted}`}>
                {track.label}
              </p>
              <nav className="space-y-0.5">
                {SECTIONS.filter((s) => s.track === track.id).map((s) => {
                  const Icon = s.icon;
                  const on = active === s.id;
                  const shown = matches(s.layers);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      disabled={!shown}
                      onClick={() => shown && scrollTo(s.id)}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] transition ${
                        !shown
                          ? "cursor-not-allowed opacity-30"
                          : on
                            ? isDark
                              ? "bg-blue-500/15 font-medium text-blue-300"
                              : "bg-blue-50 font-medium text-blue-700"
                            : isDark
                              ? "text-gray-400 hover:bg-white/[0.04] hover:text-gray-200"
                              : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                      }`}
                    >
                      <Icon size={15} className="shrink-0" />
                      {s.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          ))}
        </aside>

        {navOpen && (
          <div
            className="fixed inset-0 z-10 bg-black/40 lg:hidden"
            onClick={() => setNavOpen(false)}
          />
        )}

        {/* Content */}
        <main className="min-w-0 flex-1 px-5 py-8 sm:px-8 lg:px-12">
          <Hero isDark={isDark} heading={heading} muted={muted} />

          <LayerFilter
            isDark={isDark}
            muted={muted}
            heading={heading}
            activeLayers={activeLayers}
            toggle={toggleLayer}
            clear={clearLayers}
          />

          <TrackHeader track={TRACKS[0]} isDark={isDark} heading={heading} muted={muted} />
          <Intro {...sharedProps} />
          <Architecture {...sharedProps} />
          <DashboardUsage {...sharedProps} />
          <Quickstart {...sharedProps} />
          <Wrapper {...sharedProps} />
          <ApiReference {...sharedProps} />
          <DataShapes {...sharedProps} />

          <TrackHeader track={TRACKS[1]} isDark={isDark} heading={heading} muted={muted} />
          <Lifecycle {...sharedProps} />
          <MultiTenancy {...sharedProps} />
          <Auth {...sharedProps} />
          <CorsLayers {...sharedProps} />
          <RateLimiting {...sharedProps} />
          <Media {...sharedProps} />
          <Payments {...sharedProps} />
          <Caching {...sharedProps} />
          <Troubleshooting {...sharedProps} />

          <TrackHeader track={TRACKS[2]} isDark={isDark} heading={heading} muted={muted} />
          <Roadmap {...sharedProps} />

          <footer className={`mt-16 border-t pt-6 text-[12px] ${isDark ? "border-white/[0.06] text-gray-500" : "border-gray-200 text-gray-400"}`}>
            Zichtbaar Studio — interne documentatie. Vragen of iets klopt niet meer?
            Geef het door, dan werken we deze pagina bij.
          </footer>
        </main>
      </div>
    </div>
  );
}

/* ── Herbruikbare bouwstenen ─────────────────────────────────── */

const TagBadge = ({ layer, isDark, active = false, onClick }) => {
  const m = LAYERS[layer];
  if (!m) return null;
  const clickable = typeof onClick === "function";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      title={clickable ? `Filter op ${m.label}` : m.label}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] font-medium transition ${
        isDark ? m.dark : m.light
      } ${clickable ? "cursor-pointer hover:brightness-110" : "cursor-default"} ${
        active ? (isDark ? "ring-1 ring-white/40" : "ring-1 ring-black/30") : ""
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </button>
  );
};

const LayerFilter = ({ isDark, muted, heading, activeLayers, toggle, clear }) => {
  const box = isDark ? "border-white/[0.08] bg-white/[0.02]" : "border-gray-200 bg-white";
  return (
    <div className={`my-6 rounded-xl border p-4 ${box}`}>
      <div className="mb-2.5 flex items-center justify-between gap-3">
        <div className={`flex items-center gap-2 text-[12.5px] font-semibold ${heading}`}>
          <Filter size={14} />
          Filter op systeemlaag
        </div>
        {activeLayers.size > 0 && (
          <button
            type="button"
            onClick={clear}
            className={`text-[11.5px] font-medium ${isDark ? "text-blue-300 hover:text-blue-200" : "text-blue-600 hover:text-blue-700"}`}
          >
            Alles tonen
          </button>
        )}
      </div>
      <p className={`mb-3 text-[12px] leading-relaxed ${muted}`}>
        Elke sectie is gemarkeerd met de laag/lagen waarin ze speelt. Klik een
        laag aan om alleen die secties te tonen — handig als je gericht aan het
        frontend, de backend of de infrastructuur werkt.
      </p>
      <div className="flex flex-wrap gap-2">
        {LAYER_ORDER.map((l) => (
          <TagBadge
            key={l}
            layer={l}
            isDark={isDark}
            active={activeLayers.has(l)}
            onClick={() => toggle(l)}
          />
        ))}
      </div>
    </div>
  );
};

const TrackHeader = ({ track, isDark, heading, muted }) => (
  <div className="mb-1 mt-12 flex items-center gap-3 first:mt-6">
    <div className={`rounded-lg p-2 ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}>
      <Layers size={16} className={isDark ? "text-gray-300" : "text-gray-600"} />
    </div>
    <div>
      <h2 className={`text-[15px] font-bold uppercase tracking-wide ${heading}`}>
        {track.label}
      </h2>
      <p className={`text-[12px] ${muted}`}>{track.hint}</p>
    </div>
  </div>
);

const Section = ({ id, icon: Icon, title, layers = [], showTags = true, isDark, heading, activeLayers, onTag, children }) => {
  if (activeLayers && activeLayers.size > 0 && !layers.some((l) => activeLayers.has(l))) {
    return null;
  }
  return (
    <section id={id} className="scroll-mt-20 border-t border-dashed py-10 first:border-t-0" style={{ borderColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)" }}>
      <div className="mb-2 flex items-center gap-2.5">
        <div className={`rounded-lg p-1.5 ${isDark ? "bg-blue-500/15" : "bg-blue-50"}`}>
          <Icon size={17} className={isDark ? "text-blue-400" : "text-blue-600"} />
        </div>
        <h2 className={`text-[20px] font-bold tracking-tight ${heading}`}>{title}</h2>
      </div>
      {layers.length > 0 && showTags && (
        <div className="mb-5 flex flex-wrap gap-1.5">
          {layers.map((l) => (
            <TagBadge
              key={l}
              layer={l}
              isDark={isDark}
              active={activeLayers?.has(l)}
              onClick={onTag ? () => onTag(l) : undefined}
            />
          ))}
        </div>
      )}
      {children}
    </section>
  );
};

const P = ({ className = "", children }) => (
  <p className={`mb-3 text-[13.5px] leading-relaxed ${className}`}>{children}</p>
);

const Callout = ({ tone = "info", isDark, title, children }) => {
  const tones = {
    info: {
      bg: isDark ? "bg-blue-500/[0.07]" : "bg-blue-50",
      border: isDark ? "border-blue-500/20" : "border-blue-200",
      icon: <CheckCircle2 size={15} className={isDark ? "text-blue-400" : "text-blue-600"} />,
      titleColor: isDark ? "text-blue-300" : "text-blue-800",
    },
    warn: {
      bg: isDark ? "bg-amber-500/[0.07]" : "bg-amber-50",
      border: isDark ? "border-amber-500/20" : "border-amber-200",
      icon: <AlertTriangle size={15} className={isDark ? "text-amber-400" : "text-amber-600"} />,
      titleColor: isDark ? "text-amber-300" : "text-amber-800",
    },
  };
  const t = tones[tone];
  return (
    <div className={`my-4 rounded-xl border p-4 ${t.bg} ${t.border}`}>
      <div className={`mb-1 flex items-center gap-2 text-[13px] font-semibold ${t.titleColor}`}>
        {t.icon}
        {title}
      </div>
      <div className={`text-[12.5px] leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {children}
      </div>
    </div>
  );
};

const Step = ({ n, title, isDark, heading, children }) => (
  <div className="relative mb-6 pl-11">
    <div
      className={`absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-bold ${
        isDark ? "bg-blue-500/15 text-blue-300" : "bg-blue-100 text-blue-700"
      }`}
    >
      {n}
    </div>
    <h3 className={`mb-1.5 text-[14.5px] font-semibold ${heading}`}>{title}</h3>
    <div className={isDark ? "text-gray-400" : "text-gray-600"}>{children}</div>
  </div>
);

const Diagram = ({ isDark, muted, children }) => {
  const box = isDark ? "border-white/[0.1] bg-white/[0.03]" : "border-gray-200 bg-white";
  return (
    <div className={`my-5 overflow-x-auto rounded-xl border p-5 ${box}`}>
      <pre className={`font-mono text-[12px] leading-relaxed ${muted}`}>{children}</pre>
    </div>
  );
};

// Uitklapbaar "onder de motorkap"-blok.
const UnderHood = ({ isDark, muted, heading, title = "Onder de motorkap", children }) => {
  const box = isDark ? "border-white/[0.08] bg-white/[0.02]" : "border-gray-200 bg-white";
  return (
    <details className={`group my-4 rounded-xl border p-4 ${box}`}>
      <summary className={`cursor-pointer list-none text-[13px] font-semibold ${heading}`}>
        <span className="mr-2 text-blue-500 transition group-open:rotate-90">▸</span>
        {title}
      </summary>
      <div className={`mt-3 space-y-2 pl-5 text-[12.5px] leading-relaxed ${muted}`}>
        {children}
      </div>
    </details>
  );
};

/* ── Hero ────────────────────────────────────────────────────── */

const Hero = ({ isDark, heading, muted }) => (
  <div className="mb-2">
    <span
      className={`inline-block rounded-full px-3 py-1 text-[11px] font-medium ${
        isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-500"
      }`}
    >
      Interne developer-documentatie
    </span>
    <h1 className={`mt-4 text-[34px] font-extrabold leading-tight tracking-tight ${heading}`}>
      Bouw een klantensite op de Zichtbaar-backend
    </h1>
    <p className={`mt-3 max-w-2xl text-[15px] leading-relaxed ${muted}`}>
      Twee sporen: <strong>Snel koppelen</strong> brengt je van leeg mapje naar
      live data, <strong>Hoe het echt werkt</strong> legt uit wat er onder de
      motorkap gebeurt — multi-tenant, auth, de twee CORS-lagen, rate-limiting,
      S3 en betalingen. Filter hieronder op de laag waar je in werkt.
    </p>
  </div>
);

/* ════════════════════════════════════════════════════════════════
 *  SPOOR 1 — SNEL KOPPELEN
 * ════════════════════════════════════════════════════════════════ */

/* ── Introductie ─────────────────────────────────────────────── */

const Intro = (p) => (
  <Section id="intro" icon={BookOpen} title="Introductie" layers={[]} {...p}>
    <P className={p.text}>
      Zichtbaar Studio bestaat uit drie delen: het <strong>dashboard</strong>{" "}
      (waar klanten en wij de inhoud beheren), de <strong>backend-API</strong>{" "}
      (die alles bewaart), en de <strong>klantenwebsites</strong> (de publieke
      sites die de inhoud tonen). Deze pagina legt uit hoe je een nieuwe
      klantensite bouwt die zijn gegevens uit het dashboard haalt.
    </P>
    <P className={p.text}>
      Het belangrijkste idee: je schrijft <em>geen</em> eigen API-code per
      project. Je laadt één <strong>gedeelde SDK</strong> via een{" "}
      <code className="rounded bg-black/10 px-1 py-0.5 text-[12px] dark:bg-white/10">
        &lt;script&gt;
      </code>
      -tag, en gebruikt daarboven een dun laagje. Update ik de backend, dan
      krijgt elke site automatisch de nieuwe versie.
    </P>
    <Callout tone="info" isDark={p.isDark} title="Voor wie is dit?">
      Voor admin-collega's die een nieuwe klantenwebsite opzetten of een
      bestaande koppelen. Voor het koppelen heb je geen backend-broncode nodig —
      alleen de SDK-URL en de <code>companyId</code> van de klant. Wil je snappen
      waarom iets soms wél/niet werkt, lees dan het tweede spoor.
    </Callout>
  </Section>
);

/* ── Architectuur ────────────────────────────────────────────── */

const Architecture = (p) => {
  const { isDark, text, muted, heading } = p;
  const box = isDark ? "border-white/[0.1] bg-white/[0.03]" : "border-gray-200 bg-white";
  return (
    <Section id="architectuur" icon={Boxes} title="Hoe het samenhangt" layers={["frontend", "sdk", "backend"]} {...p}>
      <P className={text}>
        Vier onderdelen werken samen. De klantensite praat nooit rechtstreeks met
        de database — altijd via de SDK naar de publieke API-endpoints.
      </P>

      <Diagram isDark={isDark} muted={muted}>{`
   ┌─────────────────┐        beheert inhoud        ┌──────────────────┐
   │   Dashboard     │ ───────────────────────────▶ │   Backend-API    │
   │ (deze repo)     │                              │ api.zichtbaar... │
   └─────────────────┘                              └────────┬─────────┘
                                                             │ serveert
                                                             ▼
                                                    ┌──────────────────┐
   ┌─────────────────┐     <script src=sdk.js>      │     sdk.js       │
   │  Klantensite    │ ◀─────────────────────────── │ (gehoste SDK)    │
   │ (eigen project) │      window.ZichtbaarAPI     └──────────────────┘
   └─────────────────┘
`}</Diagram>

      <div className="grid gap-3 sm:grid-cols-2">
        {[
          ["Dashboard", "Deze React-app. Admins en klanten beheren hier items, producten, reservaties, popups, vacatures…"],
          ["Backend-API", `Aparte server op ${API_URL}. Bewaart alles en biedt publieke endpoints per bedrijf.`],
          ["SDK (sdk.js)", `Eén JavaScript-bestand, gehost op de backend. Bevat alle API-logica en wordt als globale window.ZichtbaarAPI geladen.`],
          ["Klantensite", "Jouw project (meestal Vite + React). Laadt de SDK en toont de inhoud van één bedrijf."],
        ].map(([t, d]) => (
          <div key={t} className={`rounded-xl border p-4 ${box}`}>
            <h3 className={`mb-1 text-[13.5px] font-semibold ${heading}`}>{t}</h3>
            <p className={`text-[12.5px] leading-relaxed ${muted}`}>{d}</p>
          </div>
        ))}
      </div>

      <Callout tone="info" isDark={isDark} title="Onthoud dit ene principe">
        De SDK bevat de logica. Jouw project bevat alleen <strong>configuratie</strong>{" "}
        (welke <code>companyId</code>) en <strong>presentatie</strong> (hoe het eruitziet).
      </Callout>
    </Section>
  );
};

/* ── Dashboard gebruiken ─────────────────────────────────────── */

const FEATURES = [
  ["Items", "items", "Vrije content-blokken (behandelingen, teamleden, locaties…). De basis van de meeste sites."],
  ["Shop / Producten", "shop", "Producten met prijzen en categorieën voor de webshop."],
  ["Bestellingen", "orders", "Inkomende orders en betalingen (Mollie)."],
  ["Reservaties", "reservations", "Boekingen met beschikbaarheid per dag."],
  ["Events", "events", "Evenementen met inschrijvingen."],
  ["Popups", "popups", "Tijdelijke melding-popups op de klantensite."],
  ["Configurator", "configurator", "Stapsgewijze product-/offerte-configurator."],
  ["Vacatures", "jobs", "Jobaanbiedingen + sollicitaties."],
  ["Tickets", "tickets", "Support-tickets."],
  ["Statistieken", "statistics", "Bezoek- en gebruikscijfers."],
  ["Inbox", "inbox", "Berichten van contactformulieren."],
  ["Bestanden", "files", "Gedeelde bestanden/downloads."],
  ["Offertes", "offertes", "Offertes opmaken en versturen."],
];

const DashboardUsage = (p) => {
  const { isDark, text, muted, heading } = p;
  const rowB = isDark ? "border-white/[0.05]" : "border-gray-100";
  return (
    <Section id="dashboard" icon={LayoutDashboard} title="Het dashboard gebruiken" layers={["backend"]} {...p}>
      <P className={text}>
        Elke klant (een <em>company</em>) logt in op het dashboard en ziet alleen
        de modules waarvoor zij rechten hebben. Welke modules zichtbaar zijn,
        bepaalt het veld <code>permissions</code> op het bedrijf.
      </P>
      <P className={text}>
        Wat een klant in het dashboard invult, komt meteen via de API beschikbaar
        voor hun website. Er is geen aparte "publiceer"-stap: opslaan = live.
      </P>

      <h3 className={`mb-2 mt-6 text-[14.5px] font-semibold ${heading}`}>Modules in één oogopslag</h3>

      {/* Tabel — vanaf sm */}
      <div className={`hidden overflow-x-auto rounded-xl border sm:block ${isDark ? "border-white/[0.08]" : "border-gray-200"}`}>
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className={`border-b ${rowB} ${isDark ? "bg-white/[0.02]" : "bg-gray-50"}`}>
              <th className={`px-3 py-2.5 text-left font-semibold ${heading}`}>Module</th>
              <th className={`px-3 py-2.5 text-left font-semibold ${heading}`}>permission-sleutel</th>
              <th className={`px-3 py-2.5 text-left font-semibold ${heading}`}>Waarvoor</th>
            </tr>
          </thead>
          <tbody>
            {FEATURES.map(([name, key, desc]) => (
              <tr key={key} className={`border-b last:border-0 ${rowB}`}>
                <td className={`px-3 py-2 font-medium ${text}`}>{name}</td>
                <td className="px-3 py-2">
                  <code className={`rounded px-1.5 py-0.5 text-[11.5px] ${isDark ? "bg-white/[0.06] text-blue-300" : "bg-blue-50 text-blue-700"}`}>
                    {key}
                  </code>
                </td>
                <td className={`px-3 py-2 ${muted}`}>{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Kaarten — onder sm */}
      <div className="space-y-2.5 sm:hidden">
        {FEATURES.map(([name, key, desc]) => (
          <div
            key={key}
            className={`rounded-xl border p-3.5 ${isDark ? "border-white/[0.08] bg-white/[0.02]" : "border-gray-200 bg-white"}`}
          >
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <span className={`text-[13.5px] font-semibold ${text}`}>{name}</span>
              <code className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] ${isDark ? "bg-white/[0.06] text-blue-300" : "bg-blue-50 text-blue-700"}`}>
                {key}
              </code>
            </div>
            <p className={`text-[12.5px] leading-relaxed ${muted}`}>{desc}</p>
          </div>
        ))}
      </div>

      <Callout tone="info" isDark={isDark} title="Rechten aanpassen?">
        Modules aan- of uitzetten gebeurt op het bedrijf via{" "}
        <strong>Companies</strong> (admin). De website hoeft daar niets voor te
        veranderen — endpoints die geen data hebben, geven gewoon een lege lijst terug.
      </Callout>
    </Section>
  );
};

/* ── Quickstart ──────────────────────────────────────────────── */

const Quickstart = (p) => {
  const { isDark, text, muted, heading } = p;
  return (
    <Section id="quickstart" icon={Rocket} title="Nieuw project starten" layers={["frontend", "sdk"]} {...p}>
      <P className={text}>
        Van leeg mapje tot een site die live data toont, in zes stappen. We gaan
        uit van <strong>Vite + React</strong> (zoals onze andere klantensites).
      </P>

      <Step n="1" title="Maak het project aan" isDark={isDark} heading={heading}>
        <CodeBlock language="bash">{`npm create vite@latest mijn-klant -- --template react
cd mijn-klant
npm install`}</CodeBlock>
      </Step>

      <Step n="2" title="Zet de configuratie in .env" isDark={isDark} heading={heading}>
        <P className={muted}>
          Twee waarden volstaan. De <code>companyId</code> krijg je uit het
          dashboard (Companies → het bedrijf). De API-URL is voor iedereen gelijk.
        </P>
        <CodeBlock filename=".env">{`VITE_API_URL=${API_URL}
VITE_COMPANY_ID=plak-hier-de-company-id
VITE_S3_BASE_URL=https://zichtbaar-images.s3.eu-north-1.amazonaws.com`}</CodeBlock>
      </Step>

      <Step n="3" title="Laad de gedeelde SDK" isDark={isDark} heading={heading}>
        <P className={muted}>
          Voeg onderaan <code>index.html</code>, vlak vóór{" "}
          <code>&lt;/body&gt;</code>, deze regel toe. Dit is de kern: één script dat
          de hele API-laag binnenhaalt.
        </P>
        <CodeBlock filename="index.html">{`  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.jsx"></script>

    <!-- Gedeelde Zichtbaar SDK — altijd de laatste versie -->
    <script src="${SDK_URL}"></script>
  </body>`}</CodeBlock>
      </Step>

      <Step n="4" title="Kopieer de API-wrapper" isDark={isDark} heading={heading}>
        <P className={muted}>
          Maak <code>src/lib/api.js</code> en plak de{" "}
          <button
            type="button"
            onClick={() => document.getElementById("wrapper")?.scrollIntoView({ behavior: "smooth" })}
            className={isDark ? "underline decoration-dotted underline-offset-2 hover:text-blue-300" : "underline decoration-dotted underline-offset-2 hover:text-blue-600"}
          >
            canonieke wrapper hieronder
          </button>
          . Dit is het enige stukje dat je per project kopieert.
        </P>
      </Step>

      <Step n="5" title="Haal data op in een component" isDark={isDark} heading={heading}>
        <CodeBlock filename="src/App.jsx">{`import { useEffect, useState } from "react";
import { getCompanyInfo, getItems } from "./lib/api";

export default function App() {
  const [company, setCompany] = useState(null);
  const [items, setItems] = useState({});

  useEffect(() => {
    (async () => {
      const [c, i] = await Promise.all([getCompanyInfo(), getItems()]);
      setCompany(c?.data ?? c);
      setItems(i?.data ?? i);
    })();
  }, []);

  if (!company) return <p>Laden…</p>;
  return <h1>{company.name}</h1>;
}`}</CodeBlock>
      </Step>

      <Step n="6" title="Start en controleer" isDark={isDark} heading={heading}>
        <CodeBlock language="bash">{`npm run dev`}</CodeBlock>
        <P className={muted}>
          Zie je de bedrijfsnaam? Dan staat de koppeling. Werkt het niet, kijk in de
          console — staat er "ZichtbaarAPI SDK not loaded", dan ontbreekt de
          script-tag uit stap 3.
        </P>
      </Step>

      <Callout tone="info" isDark={isDark} title="Liever niet handmatig?">
        In de map <code>docs/starter/</code> van deze repo staan kant-en-klare
        bestanden (<code>api.js</code>, <code>.env.example</code>,{" "}
        <code>index.html</code>-snippet, <code>README.md</code>) die je 1-op-1 kunt
        kopiëren.
      </Callout>
    </Section>
  );
};

/* ── Wrapper ─────────────────────────────────────────────────── */

const Wrapper = (p) => {
  const { isDark, text } = p;
  return (
    <Section id="wrapper" icon={Code2} title="De API-wrapper" layers={["sdk", "frontend"]} {...p}>
      <P className={text}>
        Dit is de <strong>canonieke</strong> <code>src/lib/api.js</code>. Eén
        officiële versie die iedereen kopieert. Ze proxiet naar de gehoste SDK en
        heeft voor een paar endpoints een directe <code>fetch</code>-fallback,
        zodat de site blijft werken zelfs als een oudere SDK gecachet is.
      </P>
      <CodeBlock filename="src/lib/api.js">{CANONICAL_WRAPPER}</CodeBlock>
      <Callout tone="warn" isDark={isDark} title="Belangrijk over kopiëren">
        Deze wrapper is het enige deel dat <em>niet</em> automatisch meeüpdatet (hij
        staat immers in elk project apart). Voeg ik een nieuwe SDK-methode toe, dan
        moet die hier ook bijkomen. Werk daarom altijd vanaf deze pagina — die houden
        we up-to-date — en niet vanaf een oude kopie van een vorig project.
      </Callout>
    </Section>
  );
};

/* ── API-referentie ──────────────────────────────────────────── */

const METHODS = [
  ["getCompanyInfo()", "—", "/company/public/{companyId}", "Publieke bedrijfsinfo (naam, contact, social…)."],
  ["getItems()", "—", "/items/{companyId}", "Alle content-items, gegroepeerd per categorie."],
  ["getProducts()", "—", "/products/public/{companyId}", "Producten per categorie."],
  ["getReservationAvailability(date)", "date: 'YYYY-MM-DD'", "/reservations/public/availability", "Vrije slots voor een dag."],
  ["makeReservation(data)", "reservatie-object", "/reservations/public/book", "Maakt een boeking. Alias: createReservation()."],
  ["submitContactForm(data)", "formulier-object", "/contacts/{companyId}", "Verstuurt een contactformulier."],
  ["createOrder(data, redirectUrl?)", "order + redirect", "/payment/checkout", "Start een Mollie-betaling."],
  ["submitOrderRequest(data)", "order-object", "/orders/public", "Order zonder directe betaling."],
  ["checkPaymentStatus(orderId)", "orderId", "/payment/check/{orderId}", "Status van een betaling."],
  ["getActivePopup()", "—", "/popups/active/{companyId}", "Actieve popup of null."],
  ["getJobListings()", "—", "/jobs/listings/{companyId}", "Open vacatures."],
  ["submitJobApplication(data)", "sollicitatie-object", "/jobs/apply/{companyId}", "Verstuurt een sollicitatie."],
];

const ApiReference = (p) => {
  const { isDark, text, muted, heading } = p;
  const rowB = isDark ? "border-white/[0.05]" : "border-gray-100";
  return (
    <Section id="reference" icon={Database} title="API-referentie" layers={["sdk", "backend"]} {...p}>
      <P className={text}>
        Alle methodes op <code>window.ZichtbaarAPI</code> (en dus op je wrapper).
        De <code>{"{companyId}"}</code> wordt automatisch ingevuld vanuit je config.
      </P>

      {/* Tabel — vanaf md */}
      <div className={`hidden overflow-x-auto rounded-xl border md:block ${isDark ? "border-white/[0.08]" : "border-gray-200"}`}>
        <table className="w-full text-[12px]">
          <thead>
            <tr className={`border-b ${rowB} ${isDark ? "bg-white/[0.02]" : "bg-gray-50"}`}>
              <th className={`px-3 py-2.5 text-left font-semibold ${heading}`}>Methode</th>
              <th className={`px-3 py-2.5 text-left font-semibold ${heading}`}>Parameters</th>
              <th className={`px-3 py-2.5 text-left font-semibold ${heading}`}>Endpoint</th>
              <th className={`px-3 py-2.5 text-left font-semibold ${heading}`}>Beschrijving</th>
            </tr>
          </thead>
          <tbody>
            {METHODS.map(([m, params, endpoint, desc]) => (
              <tr key={m} className={`border-b align-top last:border-0 ${rowB}`}>
                <td className="px-3 py-2">
                  <code className={`text-[11.5px] font-medium ${isDark ? "text-blue-300" : "text-blue-700"}`}>{m}</code>
                </td>
                <td className={`px-3 py-2 ${muted}`}>{params}</td>
                <td className="px-3 py-2">
                  <code className={`text-[11px] ${muted}`}>{endpoint}</code>
                </td>
                <td className={`px-3 py-2 ${muted}`}>{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Kaarten — onder md */}
      <div className="space-y-2.5 md:hidden">
        {METHODS.map(([m, params, endpoint, desc]) => (
          <div
            key={m}
            className={`rounded-xl border p-3.5 ${isDark ? "border-white/[0.08] bg-white/[0.02]" : "border-gray-200 bg-white"}`}
          >
            <code className={`block text-[12.5px] font-medium ${isDark ? "text-blue-300" : "text-blue-700"}`}>{m}</code>
            <p className={`mt-2 text-[12.5px] leading-relaxed ${muted}`}>{desc}</p>
            <div className={`mt-2.5 space-y-1 border-t pt-2.5 ${rowB}`}>
              <div className="flex gap-2">
                <span className={`shrink-0 text-[10.5px] font-semibold uppercase tracking-wide ${heading}`}>Endpoint</span>
                <code className={`min-w-0 break-all text-[11px] ${muted}`}>{endpoint}</code>
              </div>
              <div className="flex gap-2">
                <span className={`shrink-0 text-[10.5px] font-semibold uppercase tracking-wide ${heading}`}>Params</span>
                <span className={`min-w-0 text-[11.5px] ${muted}`}>{params}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <h3 className={`mb-2 mt-6 text-[14.5px] font-semibold ${heading}`}>Voorbeeld: reservatie maken</h3>
      <CodeBlock filename="reservatie.js">{`import { getReservationAvailability, makeReservation } from "./lib/api";

// 1. Vrije slots tonen
const slots = await getReservationAvailability("2026-07-10");

// 2. Boeken
const result = await makeReservation({
  date: "2026-07-10",
  time: "14:30",
  name: "Jan Janssens",
  email: "jan@example.be",
  phone: "+32 470 12 34 56",
  notes: "Eerste afspraak",
});`}</CodeBlock>

      <h3 className={`mb-2 mt-6 text-[14.5px] font-semibold ${heading}`}>Voorbeeld: betaling starten</h3>
      <CodeBlock filename="checkout.js">{`import { createOrder, checkPaymentStatus } from "./lib/api";

const { url, orderId } = await createOrder(
  { items: cart, customer: { name, email } },
  window.location.origin + "/bedankt",
);

window.location.href = url;        // ga naar Mollie
// op de bedankt-pagina:
const status = await checkPaymentStatus(orderId);`}</CodeBlock>
    </Section>
  );
};

/* ── Data & afbeeldingen ─────────────────────────────────────── */

const DataShapes = (p) => {
  const { isDark, text, muted, heading } = p;
  return (
    <Section id="data" icon={ImageIcon} title="Data & afbeeldingen" layers={["sdk", "infra"]} {...p}>
      <P className={text}>
        <code>getItems()</code> geeft een object terug met je categorieën als
        sleutels en een lijst items per categorie. Veldnamen volgen een vast
        patroon waaruit je het type kunt aflezen.
      </P>
      <CodeBlock filename="getItems() — voorbeeldvorm">{`{
  "Behandelingen": [
    {
      "id": "abc123",
      "name": "Botox",
      "Beschrijving_description": "Lange tekst…",
      "Prijs_text": "vanaf €180",
      "Foto's_image": ["companies/x/behandeling/botox-1.jpg"],
      "Categorie_select[Gezicht|Lichaam]": "Gezicht"
    }
  ],
  "Specialisten": [ /* … */ ]
}`}</CodeBlock>

      <h3 className={`mb-2 mt-5 text-[14.5px] font-semibold ${heading}`}>Veld-achtervoegsels</h3>
      <ul className={`mb-4 space-y-1.5 text-[12.5px] ${muted}`}>
        {[
          ["_text", "korte tekstregel"],
          ["_description", "lange tekst / meerdere paragrafen"],
          ["_array", "lijst van waarden"],
          ["_image", "lijst van S3-paden naar afbeeldingen/video's"],
          ["_select[A|B|C]", "keuze uit vaste opties (opties staan tussen [ ])"],
        ].map(([suf, d]) => (
          <li key={suf} className="flex gap-2">
            <code className={`rounded px-1.5 py-0.5 text-[11.5px] ${isDark ? "bg-white/[0.06] text-blue-300" : "bg-blue-50 text-blue-700"}`}>
              {suf}
            </code>
            <span>{d}</span>
          </li>
        ))}
      </ul>

      <Callout tone="warn" isDark={isDark} title="Pas op met _select-veldnamen">
        De volledige sleutel (bv.{" "}
        <code>{"Categorie_select[Gezicht|Lichaam]"}</code>) is de échte veldnaam.
        Hardcode hem niet zonder reden — als iemand in het dashboard de opties
        hernoemt, verandert de sleutel mee. Lees liever dynamisch uit.
      </Callout>

      <h3 className={`mb-2 mt-5 text-[14.5px] font-semibold ${heading}`}>Afbeeldingen tonen</h3>
      <P className={muted}>
        <code>_image</code>-velden bevatten <em>relatieve</em> S3-paden. Plak de
        S3-basis-URL ervoor:
      </P>
      <CodeBlock filename="mediaUrl.js">{`const S3 = import.meta.env.VITE_S3_BASE_URL
  || "https://zichtbaar-images.s3.eu-north-1.amazonaws.com";

export const mediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;   // al een volledige URL
  return \`\${S3}/\${path}\`;
};

// <img src={mediaUrl(item["Foto's_image"]?.[0])} />`}</CodeBlock>

      <Callout tone="warn" isDark={isDark} title="Laden de afbeeldingen niet? Denk aan S3-CORS">
        De media staan in een AWS S3-bucket met een eigen <strong>CORS-beleid</strong>.
        Een nieuw domein moet daar expliciet zijn toegelaten, anders blokkeert de
        browser het laden (vooral bij <code>fetch</code>/canvas, of strenge
        browsers). Voeg het domein van de klantensite toe aan de toegelaten
        origins (de <code>AllowedOrigins</code> in de bucket-CORS, doorgaans
        beheerd via de AWS-env). Meer hierover in{" "}
        <strong>De twee CORS-lagen</strong> en <strong>S3 &amp; media</strong>.
      </Callout>
    </Section>
  );
};

/* ════════════════════════════════════════════════════════════════
 *  SPOOR 2 — HOE HET ECHT WERKT
 * ════════════════════════════════════════════════════════════════ */

/* ── Levensloop van een verzoek ──────────────────────────────── */

const Lifecycle = (p) => {
  const { isDark, text, muted, heading } = p;
  return (
    <Section id="lifecycle" icon={Workflow} title="Levensloop van een verzoek" layers={["frontend", "sdk", "backend", "database", "infra"]} {...p}>
      <P className={text}>
        Eén simpele <code>getItems()</code> ziet er aan de buitenkant uit als één
        regel, maar passeert binnenin meerdere poorten. Snap je die volgorde, dan
        snap je meteen waarom iets soms faalt — bijna altijd op een van de
        infrastructuur-poorten, zelden in de business-logica.
      </P>

      <Diagram isDark={isDark} muted={muted}>{`
 Browser          Infra (poorten)         Backend-API        Data
 (klantensite)
    │ fetch /items/{companyId}
    ├───────────▶ ① CORS-check ──▶ ② rate-limit ──▶ ③ companyId-scope
    │             origin toegel.?   ≤100 / 60s / IP   (alleen dít bedrijf)
    │                                                      │
    │                                                      ▼
    │                                                 ④ DB-query
    │                                                      │
    │ ◀───────── ⑤ JSON: items + relatieve S3-paden ──────┘
    │
    │ <img src = S3-basis + pad>
    └───────────▶ ⑥ S3-bucket  (eigen, aparte CORS-laag)
`}</Diagram>

      <ol className={`mb-4 space-y-2 text-[13px] leading-relaxed ${text}`}>
        <li><strong>① CORS-check</strong> — staat het domein van de site in de API-allowlist (<code>CORS_ORIGINS</code>)? Zo niet, blokkeert de browser nog vóór er data komt.</li>
        <li><strong>② Rate-limit</strong> — max ~100 verzoeken per 60s per IP. Bezoekers achter gedeeld NAT delen die teller.</li>
        <li><strong>③ companyId-scope</strong> — de backend filtert álles op de <code>companyId</code> uit de URL: multi-tenant isolatie.</li>
        <li><strong>④ DB-query</strong> — pas hier komt de eigenlijke data uit de database.</li>
        <li><strong>⑤ Antwoord</strong> — JSON met <em>relatieve</em> S3-paden (geen volledige image-URL's).</li>
        <li><strong>⑥ Media</strong> — de browser haalt de beelden apart bij S3 op; dat is een <em>tweede</em>, losse CORS-laag.</li>
      </ol>

      <Callout tone="info" isDark={isDark} title="Waarom dit ertoe doet">
        "Werkt bij mij, niet bij sommige bezoekers" is bijna nooit de code. Het is
        poort ①, ② of ⑥ — getagd <em>Infra</em>. De volgende secties pakken die één
        voor één.
      </Callout>
    </Section>
  );
};

/* ── Multi-tenancy ───────────────────────────────────────────── */

const MultiTenancy = (p) => {
  const { isDark, text, muted, heading } = p;
  return (
    <Section id="multitenancy" icon={Building2} title="Multi-tenant: één backend, veel klanten" layers={["backend", "database"]} {...p}>
      <P className={text}>
        Er draait <strong>één</strong> backend en <strong>één</strong> database voor
        álle klanten samen. Elke klant is een <em>company</em> met een eigen{" "}
        <code>companyId</code>. Die id is de sleutel die alles van elkaar scheidt:
        elke publieke query filtert erop, zodat klant A nooit de data van klant B ziet.
      </P>

      <Diagram isDark={isDark} muted={muted}>{`
                       ┌──────────────────────────┐
   site klant A ──────▶│        Backend-API       │
   (companyId = A)     │                          │
                       │   WHERE company_id = ?   │──▶ ┌─────────────┐
   site klant B ──────▶│   (altijd gefilterd)     │    │  Database   │
   (companyId = B)     │                          │    │ (gedeeld)   │
                       └──────────────────────────┘    └─────────────┘
`}</Diagram>

      <P className={text}>
        Het <code>permissions</code>-veld op de company bepaalt daarbovenop welke
        modules een klant heeft. Heeft een klant geen <code>reservations</code>?
        Dan geeft dat endpoint gewoon een lege lijst — geen fout. Zo kun je
        dezelfde wrapper en dezelfde UI-componenten voor elke klant gebruiken.
      </P>

      <UnderHood isDark={isDark} muted={muted} heading={heading}>
        <p>
          Omdat <code>companyId</code> in de <em>URL</em> zit (niet in een token),
          zijn de publieke read-endpoints bewust open: iedereen kan de publieke
          inhoud van een bedrijf opvragen. Dat is OK voor wat sowieso op de
          website staat — maar betekent ook dat je nooit gevoelige data via een
          publiek endpoint mag teruggeven. Beheer-acties lopen via aparte,
          geauthenticeerde endpoints (zie <strong>Auth &amp; rechten</strong>).
        </p>
      </UnderHood>
    </Section>
  );
};

/* ── Auth & rechten ──────────────────────────────────────────── */

const Auth = (p) => {
  const { isDark, text, muted, heading } = p;
  const box = isDark ? "border-white/[0.1] bg-white/[0.03]" : "border-gray-200 bg-white";
  return (
    <Section id="auth" icon={KeyRound} title="Auth & rechten" layers={["backend"]} {...p}>
      <P className={text}>
        Er zijn twee soorten endpoints, en het is belangrijk te weten welk soort
        je raakt. Een klantensite gebruikt <em>alleen</em> de publieke soort — je
        zet er dus nooit een wachtwoord of token in de frontend.
      </P>

      <div className="my-4 grid gap-3 sm:grid-cols-2">
        <div className={`rounded-xl border p-4 ${box}`}>
          <h3 className={`mb-1.5 text-[13.5px] font-semibold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
            Publiek (klantensite)
          </h3>
          <ul className={`list-disc space-y-1 pl-4 text-[12.5px] ${muted}`}>
            <li>Geen login, geen token</li>
            <li><code>companyId</code> staat in de URL</li>
            <li>Lezen + welbepaalde "verstuur"-acties (contact, boeking, sollicitatie, betaling)</li>
            <li>Dit is wat de SDK gebruikt</li>
          </ul>
        </div>
        <div className={`rounded-xl border p-4 ${box}`}>
          <h3 className={`mb-1.5 text-[13.5px] font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
            Beveiligd (dashboard)
          </h3>
          <ul className={`list-disc space-y-1 pl-4 text-[12.5px] ${muted}`}>
            <li>JWT-token vereist (login op het dashboard)</li>
            <li>Beheer: aanmaken, bewerken, verwijderen</li>
            <li>Rechten via <code>permissions</code> en <code>isAdmin</code></li>
            <li>Nooit vanuit een klantensite aanroepen</li>
          </ul>
        </div>
      </div>

      <Callout tone="warn" isDark={isDark} title="Gouden regel">
        Een klantensite mag enkel publieke endpoints raken. Heb je een token nodig
        om iets te tonen, dan zit het op de verkeerde laag — dat hoort in het
        dashboard, niet op de publieke site.
      </Callout>
    </Section>
  );
};

/* ── De twee CORS-lagen ──────────────────────────────────────── */

const CorsLayers = (p) => {
  const { isDark, text, muted, heading } = p;
  const box = isDark ? "border-white/[0.1] bg-white/[0.03]" : "border-gray-200 bg-white";
  return (
    <Section id="cors" icon={Shield} title="De twee CORS-lagen" layers={["infra", "backend"]} {...p}>
      <P className={text}>
        Dit is dé bron van "werkt voor mij, niet voor sommigen". Er zijn{" "}
        <strong>twee aparte</strong> CORS-muren, op twee verschillende plekken. Een
        nieuw klantdomein moet op <em>allebei</em> worden toegelaten — vergeet je
        er één, dan faalt precies dat ene deel (data óf beelden).
      </P>

      <div className="my-4 grid gap-3 sm:grid-cols-2">
        <div className={`rounded-xl border p-4 ${box}`}>
          <h3 className={`mb-1.5 flex items-center gap-1.5 text-[13.5px] font-semibold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
            <Shield size={15} /> 1. API-CORS
          </h3>
          <p className={`mb-2 text-[12.5px] leading-relaxed ${muted}`}>
            Op de backend. Bepaalt welke domeinen <code>api.zichtbaar…</code> mogen
            aanroepen. Staat in de <code>CORS_ORIGINS</code>-env als{" "}
            <strong>exacte</strong> lijst.
          </p>
          <p className={`text-[12px] ${muted}`}>
            Faalt → <em>geen data</em> ("CORS-fout" in console bij een API-call).
          </p>
        </div>
        <div className={`rounded-xl border p-4 ${box}`}>
          <h3 className={`mb-1.5 flex items-center gap-1.5 text-[13.5px] font-semibold ${isDark ? "text-amber-300" : "text-amber-700"}`}>
            <Server size={15} /> 2. S3-bucket-CORS
          </h3>
          <p className={`mb-2 text-[12.5px] leading-relaxed ${muted}`}>
            Op AWS. Bepaalt welke domeinen de <strong>media</strong> uit de
            S3-bucket mogen laden. Staat in het <code>AllowedOrigins</code>-CORS-beleid
            van de bucket (beheerd via de AWS-env).
          </p>
          <p className={`text-[12px] ${muted}`}>
            Faalt → <em>geen beelden</em> (data komt wél binnen).
          </p>
        </div>
      </div>

      <Callout tone="warn" isDark={isDark} title="Exacte match — let op de varianten">
        Beide lijsten matchen <strong>exact</strong>. <code>https://klant.be</code>,{" "}
        <code>https://www.klant.be</code> en een preview-/staging-domein zijn drie
        verschillende origins. Zet bij voorkeur <strong>apex én www</strong> (en het
        preview-domein) op beide lijsten, anders voelt het grillig: het hangt af van
        via welke URL de bezoeker binnenkomt, niet van wie hij is.
      </Callout>

      <Callout tone="info" isDark={isDark} title="Nieuw domein live zetten — checklist">
        <ul className="list-disc space-y-1 pl-4">
          <li>Domein (apex + www) toevoegen aan <code>CORS_ORIGINS</code> (API).</li>
          <li>Hetzelfde domein toevoegen aan <code>AllowedOrigins</code> van de S3-bucket (AWS-env).</li>
          <li>Geef beide gewoon door, dan zet ik ze erbij.</li>
        </ul>
      </Callout>
    </Section>
  );
};

/* ── Rate-limiting ───────────────────────────────────────────── */

const RateLimiting = (p) => {
  const { isDark, text, muted, heading } = p;
  return (
    <Section id="ratelimit" icon={Gauge} title="Rate-limiting & betrouwbaarheid" layers={["infra", "backend"]} {...p}>
      <P className={text}>
        De backend heeft een globale <strong>rate-limit</strong> per IP (ruwweg 100
        verzoeken per 60 seconden), die ook publieke read-endpoints dekt. In je eentje
        testen raak je die nooit — maar in het echt kan hij willekeurige{" "}
        <code>429</code>-fouten geven.
      </P>

      <Callout tone="warn" isDark={isDark} title="Waarom het 'soms' faalt">
        Bezoekers achter gedeeld NAT/CGNAT (bedrijfsnetwerk, mobiel, school) delen
        één IP en dus één teller. Staat <code>app.proxy</code> niet aan, dan ziet de
        backend bovendien het proxy-IP i.p.v. de echte bezoeker, waardoor iederéén
        op dezelfde teller telt. Veel parallelle calls bij paginalaadtijd kunnen zo
        in een 429 lopen.
      </Callout>

      <h3 className={`mb-2 mt-5 text-[14.5px] font-semibold ${heading}`}>Wat je aan de site-kant kunt doen</h3>
      <ul className={`mb-4 list-disc space-y-1.5 pl-5 text-[13px] ${muted}`}>
        <li>Bundel calls: gebruik <code>Promise.all</code> i.p.v. tientallen losse fetches.</li>
        <li>Haal data één keer op en geef ze door, niet per component opnieuw.</li>
        <li>Toon de échte <code>error.message</code> (niet een generiek "Er ging iets mis") — anders is een 429 niet te onderscheiden van een echte fout.</li>
      </ul>

      <UnderHood isDark={isDark} muted={muted} heading={heading}>
        <p>
          De limiet en het proxy-gedrag zijn server-instellingen (koa-ratelimit +{" "}
          <code>app.proxy</code> in de backend). Loop je er structureel tegenaan voor
          een drukke klant, dan kunnen we de limiet verhogen of per-route verfijnen —
          geef het door met wat context (welke pagina, hoeveel bezoekers).
        </p>
      </UnderHood>
    </Section>
  );
};

/* ── S3 & media ──────────────────────────────────────────────── */

const Media = (p) => {
  const { isDark, text, muted, heading } = p;
  return (
    <Section id="media" icon={Server} title="S3 & media onder de motorkap" layers={["infra"]} {...p}>
      <P className={text}>
        Alle afbeeldingen en video's staan in een AWS S3-bucket (regio{" "}
        <code>eu-north-1</code>). De API geeft alleen <em>relatieve</em> paden terug;
        de volledige URL bouw je zelf met de S3-basis ervoor (zie{" "}
        <strong>Data &amp; afbeeldingen</strong>).
      </P>

      <h3 className={`mb-2 mt-4 text-[14.5px] font-semibold ${heading}`}>Drie dingen die misgaan</h3>
      <ul className={`mb-4 space-y-2 text-[13px] leading-relaxed ${muted}`}>
        <li><strong>Basis-URL vergeten</strong> — een rauw pad zoals <code>companies/x/foto.jpg</code> is geen geldige <code>src</code>. Plak <code>VITE_S3_BASE_URL</code> ervoor.</li>
        <li><strong>S3-CORS</strong> — het domein moet in <code>AllowedOrigins</code> van de bucket staan, anders blokkeert de browser (zie <strong>De twee CORS-lagen</strong>).</li>
        <li><strong>Slashes in image-id's</strong> — gebruik je een image-id ín een API-URL, dan moet die <code>encodeURIComponent</code>-encoded worden (S3-keys bevatten <code>/</code>).</li>
      </ul>

      <Callout tone="info" isDark={isDark} title="Video's net als foto's">
        <code>_image</code>-velden kunnen ook video's bevatten. Het zijn altijd
        lijsten — pak <code>[0]</code>, of loop erover als je een galerij toont.
      </Callout>
    </Section>
  );
};

/* ── Mollie-betalingen ───────────────────────────────────────── */

const Payments = (p) => {
  const { isDark, text, muted, heading } = p;
  return (
    <Section id="payments" icon={CreditCard} title="Mollie-betalingen" layers={["payment", "backend"]} {...p}>
      <P className={text}>
        Betalingen lopen via <strong>Mollie</strong>, maar je site praat nooit
        rechtstreeks met Mollie. Je vraagt de backend om een betaling te starten,
        stuurt de bezoeker naar de Mollie-betaalpagina, en checkt daarna de status.
      </P>

      <Diagram isDark={isDark} muted={muted}>{`
  Klantensite                    Backend-API              Mollie
      │ createOrder(data, redirect)
      ├──────────────────────────▶ maakt order + payment ──▶ checkout-URL
      │ ◀──────── { url, orderId } ──────────────────────────┘
      │
      │ window.location = url   (bezoeker betaalt bij Mollie)
      │                                   │
      │                          webhook  ▼
      │                         Backend werkt status bij  ◀── Mollie
      │
      │ terug op /bedankt:
      └─ checkPaymentStatus(orderId) ─▶ Backend ─▶ "paid" / "open" / "failed"
`}</Diagram>

      <ul className={`mb-4 space-y-2 text-[13px] leading-relaxed ${muted}`}>
        <li><strong>Start:</strong> <code>createOrder(data, redirectUrl)</code> geeft <code>{"{ url, orderId }"}</code>. Stuur de bezoeker naar <code>url</code>.</li>
        <li><strong>Bevestiging:</strong> de échte status komt via een Mollie-<em>webhook</em> naar de backend — niet via de browser. De redirect alleen bewijst geen betaling.</li>
        <li><strong>Check:</strong> op je bedankt-pagina roep je <code>checkPaymentStatus(orderId)</code> aan om de status te tonen.</li>
      </ul>

      <Callout tone="warn" isDark={isDark} title="Vertrouw nooit op de redirect alleen">
        Een bezoeker kan de betaalpagina sluiten of terugkeren zonder te betalen.
        Beslis "betaald ja/nee" altijd op basis van de status uit de backend
        (gevoed door de webhook), niet op het feit dat hij op <code>/bedankt</code> belandt.
      </Callout>
    </Section>
  );
};

/* ── SDK-caching & updates ───────────────────────────────────── */

const Caching = (p) => {
  const { isDark, text, muted, heading } = p;
  return (
    <Section id="caching" icon={RefreshCw} title="SDK-caching & updates" layers={["sdk", "infra"]} {...p}>
      <P className={text}>
        De grote winst van deze opzet: omdat elke site de SDK via een{" "}
        <code>&lt;script&gt;</code>-tag laadt, krijgt iedereen automatisch de
        nieuwste backend-logica zodra ik <code>sdk.js</code> op de server vervang.
        Geen <code>npm install</code>, geen herdeploy per klant.
      </P>

      <div className="my-4 grid gap-3 sm:grid-cols-2">
        <div className={`rounded-xl border p-4 ${isDark ? "border-green-500/20 bg-green-500/[0.05]" : "border-green-200 bg-green-50"}`}>
          <h3 className={`mb-1.5 flex items-center gap-1.5 text-[13.5px] font-semibold ${isDark ? "text-green-300" : "text-green-800"}`}>
            <CheckCircle2 size={15} /> Updatet automatisch
          </h3>
          <ul className={`list-disc space-y-1 pl-4 text-[12.5px] ${muted}`}>
            <li>Alle logica binnen de SDK</li>
            <li>Nieuwe velden in bestaande antwoorden</li>
            <li>Bugfixes en endpoint-aanpassingen achter een bestaande methode</li>
          </ul>
        </div>
        <div className={`rounded-xl border p-4 ${isDark ? "border-amber-500/20 bg-amber-500/[0.05]" : "border-amber-200 bg-amber-50"}`}>
          <h3 className={`mb-1.5 flex items-center gap-1.5 text-[13.5px] font-semibold ${isDark ? "text-amber-300" : "text-amber-800"}`}>
            <AlertTriangle size={15} /> Vraagt handwerk
          </h3>
          <ul className={`list-disc space-y-1 pl-4 text-[12.5px] ${muted}`}>
            <li>Een <em>nieuwe</em> methode toevoegen aan de wrapper</li>
            <li>De directe <code>fetch</code>-fallbacks in de wrapper</li>
            <li>Andere gegevensvorm waar de UI op rekent</li>
          </ul>
        </div>
      </div>

      <Callout tone="warn" isDark={isDark} title="Caching-tip">
        Browsers en CDN's kunnen <code>sdk.js</code> tijdelijk cachen. Zie je na een
        backend-update niet meteen het nieuwe gedrag, doe dan een hard refresh
        (Ctrl/Cmd + Shift + R). Wil je dit helemaal vermijden, dan kunnen we de SDK
        gaan versioneren (bv. <code>sdk.js?v=2</code>) — laat het weten als dat nodig is.
      </Callout>
    </Section>
  );
};

/* ── Troubleshooting ─────────────────────────────────────────── */

const TROUBLE = [
  [
    "\"ZichtbaarAPI SDK not loaded\" in de console",
    "De script-tag ontbreekt of laadt te laat. Controleer dat <script src=\"…/sdk.js\"></script> in index.html staat, ná je app-script, en dat de URL bereikbaar is.",
  ],
  [
    "Lege data terwijl het dashboard wél is ingevuld",
    "Verkeerde companyId, of de klant heeft geen rechten op die module. Check VITE_COMPANY_ID en de permissions van het bedrijf.",
  ],
  [
    "CORS-fout bij een API-call (geen data)",
    "Laag 1: het domein van de klantensite staat niet (exact) in CORS_ORIGINS van de backend. Let op apex vs www. Geef het domein door, dan zetten we het op de allowlist. Zie 'De twee CORS-lagen'.",
  ],
  [
    "Afbeeldingen laden niet (data komt wél binnen)",
    "Twee oorzaken: (a) je vergat de S3-basis-URL vóór het pad te zetten — _image is altijd een lijst, pak [0]; (b) laag 2: het domein staat niet in het CORS-beleid (AllowedOrigins) van de AWS S3-bucket. Geef het door, dan zetten we het erbij.",
  ],
  [
    "Willekeurige 429 / werkt soms wel, soms niet",
    "Rate-limit per IP. Bezoekers achter gedeeld NAT delen de teller. Bundel calls (Promise.all), haal data één keer op. Structureel? Dan verhogen we de limiet. Zie 'Rate-limiting'.",
  ],
  [
    "Betaling 'gelukt' maar order niet betaald",
    "Vertrouw niet op de redirect naar /bedankt. De status komt via de Mollie-webhook in de backend — lees ze met checkPaymentStatus(orderId). Zie 'Mollie-betalingen'.",
  ],
  [
    "Backend bijgewerkt maar site toont oud gedrag",
    "Gecachte sdk.js. Hard refresh (Ctrl/Cmd+Shift+R). Blijft het hangen, dan kunnen we de SDK versioneren.",
  ],
];

const Troubleshooting = (p) => {
  const { isDark, muted, heading } = p;
  const box = isDark ? "border-white/[0.08] bg-white/[0.02]" : "border-gray-200 bg-white";
  return (
    <Section id="troubleshooting" icon={LifeBuoy} title="Problemen oplossen" layers={["frontend", "sdk", "backend", "database", "infra", "payment"]} {...p}>
      <div className="space-y-2.5">
        {TROUBLE.map(([q, a]) => (
          <details key={q} className={`group rounded-xl border p-4 ${box}`}>
            <summary className={`cursor-pointer list-none text-[13.5px] font-semibold ${heading}`}>
              <span className="mr-2 text-blue-500">▸</span>
              {q}
            </summary>
            <p className={`mt-2 pl-5 text-[12.5px] leading-relaxed ${muted}`}>{a}</p>
          </details>
        ))}
      </div>

      <div className={`mt-6 rounded-xl border p-5 ${isDark ? "border-blue-500/20 bg-blue-500/[0.05]" : "border-blue-200 bg-blue-50"}`}>
        <h3 className={`mb-1 flex items-center gap-1.5 text-[14px] font-semibold ${isDark ? "text-blue-300" : "text-blue-800"}`}>
          <ExternalLink size={15} /> Nog vragen?
        </h3>
        <p className={`text-[12.5px] leading-relaxed ${muted}`}>
          Klopt iets niet meer of mis je een endpoint? Geef het door — dan werk ik
          de backend en deze pagina bij, zodat iedereen weer met de juiste info werkt.
        </p>
      </div>
    </Section>
  );
};

/* ════════════════════════════════════════════════════════════════
 *  SPOOR 3 — ROADMAP
 * ════════════════════════════════════════════════════════════════ */

const ROADMAP_STATUS = {
  bezig: {
    label: "Bezig",
    light: "bg-green-50 text-green-700 border-green-200",
    dark: "bg-green-500/10 text-green-300 border-green-500/25",
  },
  gepland: {
    label: "Gepland",
    light: "bg-blue-50 text-blue-700 border-blue-200",
    dark: "bg-blue-500/10 text-blue-300 border-blue-500/25",
  },
  idee: {
    label: "Idee",
    light: "bg-gray-100 text-gray-600 border-gray-200",
    dark: "bg-white/[0.06] text-gray-400 border-white/10",
  },
};

// Prioriteit (MoSCoW) — getoond als label per item.
const ROADMAP_PRIORITY = {
  must: {
    label: "Must have",
    omschrijving: "Kritiek — raakt betrouwbaarheid of omzet. Moet gebeuren.",
    light: "border-red-200 bg-red-50",
    dark: "border-red-500/25 bg-red-500/[0.07]",
    dot: "bg-red-500",
    titleLight: "text-red-700",
    titleDark: "text-red-300",
  },
  should: {
    label: "Should have",
    omschrijving: "Belangrijk — sterk gewenst, maar niet blokkerend.",
    light: "border-amber-200 bg-amber-50",
    dark: "border-amber-500/25 bg-amber-500/[0.07]",
    dot: "bg-amber-500",
    titleLight: "text-amber-700",
    titleDark: "text-amber-300",
  },
  could: {
    label: "Nice to have",
    omschrijving: "Waardevol als er tijd is — comfort en polish.",
    light: "border-blue-200 bg-blue-50",
    dark: "border-blue-500/25 bg-blue-500/[0.07]",
    dot: "bg-blue-500",
    titleLight: "text-blue-700",
    titleDark: "text-blue-300",
  },
};

// Standaard leiden we de prioriteit af van het risico. Wil je een item
// bewust anders prioriteren, zet dan een expliciete `prioriteit` op het item.
const RISK_TO_PRIORITY = { hoog: "must", middel: "should", laag: "could" };

// Elk item: { titel, kort, status, risico, probleem, oplossing, gevolg, prioriteit? }
// In data per laag (handig voor de filterbalk); op het bord hergroeperen we
// naar prioriteit. Klik een kaart open voor de volledige uitleg.
const ROADMAP = [
  {
    layer: "frontend",
    items: [
      {
        titel: "Gedeeld starter-/componentpakket",
        kort: "Nu kopieert elke site de wrapper en helpers apart.",
        status: "idee",
        risico: "middel",
        probleem:
          "Elke nieuwe klantensite begint met het kopiëren van api.js, de mediaUrl-helper en wat boilerplate. Na verloop van tijd lopen die kopieën uiteen: project A krijgt een bugfix die project B nooit ziet. Een SDK-wijziging moet je handmatig in elk project naloggen.",
        oplossing:
          "Eén gedeeld pakket (npm-package of template-repo) met de canonieke wrapper, helpers en basisopzet. Nieuwe sites starten ervan; updates trek je centraal door met één versiebump i.p.v. project per project.",
        gevolg:
          "Doen we niets, dan groeit de onderhoudslast met elke klant en sluipen er subtiele gedragsverschillen tussen sites — moeilijk te debuggen want 'het werkt bij de andere klant wel'.",
      },
      {
        titel: "Echte foutmeldingen tonen",
        kort: "Niet alles platslaan tot 'Er ging iets mis'.",
        status: "gepland",
        risico: "middel",
        probleem:
          "Veel sites vangen elke fout af tot een generiek 'Er ging iets mis'. Daardoor is een 429 (rate-limit), een CORS-blok en een validatiefout van buitenaf niet uit elkaar te houden. Diagnose op afstand wordt zo zo goed als onmogelijk.",
        oplossing:
          "De echte error.message van de backend tonen of minstens loggen, eventueel met een foutcode. De backend stuurt al { error, message } mee — die info wordt nu weggegooid.",
        gevolg:
          "Blijft dit staan, dan kost support veel meer tijd: 'het werkt niet' bevat geen enkele bruikbare aanwijzing en elke melding wordt giswerk.",
      },
      {
        titel: "Type-hints voor API-antwoorden",
        kort: "TS-/JSDoc-types op de wrapper.",
        status: "idee",
        risico: "laag",
        probleem:
          "Veldnamen als Foto's_image of Categorie_select[…] zijn losse strings. Een typfout faalt stil: je krijgt undefined i.p.v. een waarschuwing, en merkt het pas als de UI leeg blijft.",
        oplossing:
          "TS- of JSDoc-types op de wrapper-methodes en de bekende velden, met autocomplete in de editor.",
        gevolg:
          "Laag risico — vooral een kwaliteits- en snelheidswinst. Zonder dit blijven typfouten een terugkerende, kleine tijdvreter.",
      },
    ],
  },
  {
    layer: "sdk",
    items: [
      {
        titel: "SDK versioneren",
        kort: "sdk.js?v=N tegen caching-verwarring.",
        status: "gepland",
        risico: "middel",
        probleem:
          "sdk.js wordt door browsers en CDN's gecachet. Na een backend-update zien sommige bezoekers nog dagenlang het oude gedrag. 'Hard refresh' lost dat op voor jou, maar niet voor echte bezoekers.",
        oplossing:
          "Een versie in de URL (sdk.js?v=N) of een content-hash, zodat een nieuwe versie automatisch een nieuwe URL is en de cache wordt omzeild.",
        gevolg:
          "Zonder dit krijg je na elke update 'werkt bij jou, niet bij mij'-meldingen die vanzelf 'genezen' — lastig te reproduceren en vertrouwensondermijnend.",
      },
      {
        titel: "Consistente return-vorm",
        kort: "Soms { data }, soms direct het object.",
        status: "idee",
        risico: "laag",
        probleem:
          "Sommige methodes geven { data: … } terug, andere het object rechtstreeks. Daarom staat overal de defensieve 'c?.data ?? c'-truc in de wrapper en in componenten.",
        oplossing:
          "Eén afgesproken envelope (bv. altijd { data }) over alle methodes. De wrapper wordt simpeler en voorspelbaarder, en de truc kan weg.",
        gevolg:
          "Laag risico, maar het is een stille bron van bugs: bij elke nieuwe methode kan iemand de verkeerde vorm verwachten.",
      },
      {
        titel: "Getypeerde SDK (.d.ts)",
        kort: "Autocomplete op window.ZichtbaarAPI.",
        status: "idee",
        risico: "laag",
        probleem:
          "De SDK is één globale zonder types. Welke methodes bestaan en welke parameters ze nemen, moet je uit deze docs of uit het geheugen halen.",
        oplossing:
          "Een .d.ts-typedefinitie meeleveren zodat editors autocomplete en signaturen tonen voor window.ZichtbaarAPI en de wrapper.",
        gevolg:
          "Laag risico — comfort en minder fouten. Zonder dit blijft de API-referentie hieronder de enige bron van waarheid.",
      },
    ],
  },
  {
    layer: "backend",
    items: [
      {
        titel: "Server-side validatie op publieke POST's",
        kort: "Checken vóór de DB-insert.",
        status: "gepland",
        risico: "hoog",
        probleem:
          "Publieke POST's (contact, boeking, configurator-submit) gaan met een rauwe body de database in. Lange tekst of 4-byte emoji kan een rauwe 400 of insert-fout geven — net bij echte gebruikers, niet bij jouw nette testinput.",
        oplossing:
          "Een validatie- en sanitisatielaag vóór de insert: verplichte velden, types en maximale lengtes controleren, en een nette { error, message } teruggeven i.p.v. een 400/500.",
        gevolg:
          "Hoog risico — dit is een directe oorzaak van mislukte consumer-flows: verloren leads, boekingen en sollicitaties die de klant nooit ziet.",
      },
      {
        titel: "Uniform error-envelope",
        kort: "Overal { error, message, code }.",
        status: "idee",
        risico: "middel",
        probleem:
          "Verschillende endpoints geven fouten in verschillende vormen terug. De frontend kan ze daardoor niet betrouwbaar tonen of classificeren.",
        oplossing:
          "Eén vaste foutvorm { error, message, code } over alle endpoints, zodat de frontend (zie 'Echte foutmeldingen tonen') er consistent op kan bouwen.",
        gevolg:
          "Middel risico — versterkt het frontend-probleem: zolang fouten ongelijk zijn, blijft nette foutweergave lastig.",
      },
      {
        titel: "Per-route rate-limits",
        kort: "Niet één botte globale limiet.",
        status: "idee",
        risico: "middel",
        probleem:
          "Er is één globale rate-limit per IP die ook gewone read-endpoints dekt. Een drukke pagina met veel parallelle calls kan die teller onnodig opvreten.",
        oplossing:
          "Aparte limieten voor read- vs. write-endpoints (en eventueel hogere limieten voor publieke leesacties), zodat normaal browsen niet wordt afgestraft.",
        gevolg:
          "Middel risico — hangt samen met de 429-problematiek bij volume (zie laag Infra → app.proxy).",
      },
    ],
  },
  {
    layer: "database",
    items: [
      {
        titel: "utf8mb4 overal + ruimere VARCHAR's",
        kort: "Emoji en lange input zonder insert-fout.",
        status: "gepland",
        risico: "hoog",
        probleem:
          "Niet alle kolommen zijn utf8mb4 of ruim genoeg. Een emoji of lange tekst kan een insert breken — onzichtbaar tot een echte bezoeker het intikt in een formulier.",
        oplossing:
          "Migratie naar utf8mb4 op de tekstkolommen en de VARCHAR-lengtes nakijken en waar nodig verruimen.",
        gevolg:
          "Hoog risico — samen met de backend-validatie is dit de kern van 'het verzenden lukte soms niet'. Apart opgelost blijft de andere helft falen.",
      },
      {
        titel: "Indexen op company_id",
        kort: "Snel blijven bij meer klanten.",
        status: "idee",
        risico: "laag",
        probleem:
          "Elke publieke query filtert op company_id. Zonder index wordt dat trager naarmate er meer bedrijven en data bijkomen.",
        oplossing:
          "Indexen op company_id (en veelgebruikte combinaties) op de relevante tabellen.",
        gevolg:
          "Laag risico nu, maar het sluipt erin: pas merkbaar als het volume groeit, en dan onder belasting.",
      },
      {
        titel: "Audit / soft-delete",
        kort: "Markeren i.p.v. hard wissen.",
        status: "idee",
        risico: "middel",
        probleem:
          "Verwijderen is definitief. Per ongeluk weggegooide data is niet terug te halen, en er is geen spoor van wie-wat-wanneer.",
        oplossing:
          "Soft-delete (een verwijderd-vlag) plus een audit-spoor op gevoelige acties.",
        gevolg:
          "Middel risico — zeldzaam maar onomkeerbaar wanneer het misgaat (foutief verwijderde klantdata).",
      },
    ],
  },
  {
    layer: "infra",
    items: [
      {
        titel: "Eén plek om een domein te onboarden",
        kort: "CORS_ORIGINS én S3 AllowedOrigins samen.",
        status: "gepland",
        risico: "hoog",
        probleem:
          "Een nieuw domein moet nu apart in CORS_ORIGINS (API) én in AllowedOrigins (S3). Vergeet je er één, dan werkt de helft: de data laadt wél maar de beelden niet, of omgekeerd. Het voelt grillig en is moeilijk te herkennen.",
        oplossing:
          "Eén bron, script of checklist die beide tegelijk zet — inclusief apex, www en het preview-domein.",
        gevolg:
          "Hoog risico — dit is de klassieke 'live gezet maar beelden/data laden niet'-launchfout, precies op het moment dat de klant meekijkt.",
      },
      {
        titel: "app.proxy correct zetten",
        kort: "Rate-limit op het échte bezoeker-IP.",
        status: "bezig",
        risico: "hoog",
        probleem:
          "Zonder app.proxy ziet de backend het proxy-IP i.p.v. de echte bezoeker. Alle bezoekers delen dan één rate-limit-teller, wat bij volume willekeurige 429's oplevert.",
        oplossing:
          "app.proxy aanzetten en de trust-configuratie nakijken, zodat de rate-limit per echte bezoeker telt i.p.v. per proxy.",
        gevolg:
          "Hoog risico — raakt elke drukke klantensite tegelijk en is bijna niet te reproduceren in een solo-test vanaf één IP.",
      },
      {
        titel: "CDN + beeldoptimalisatie",
        kort: "CloudFront vóór S3 + resize/compressie.",
        status: "idee",
        risico: "laag",
        probleem:
          "Beelden worden rechtstreeks en op volle grootte vanaf S3 geladen. Op tragere verbindingen of zware galerijen kost dat laadtijd.",
        oplossing:
          "Een CDN (CloudFront) vóór S3 en automatische resize/compressie van afbeeldingen.",
        gevolg:
          "Laag risico — vooral snelheids- en kostenwinst. Geen blokkades, wel een merkbaar betere klantbeleving.",
      },
    ],
  },
  {
    layer: "payment",
    items: [
      {
        titel: "Bevestigingsmail mag niet stil falen",
        kort: "201 terwijl de mail nooit vertrok.",
        status: "gepland",
        risico: "hoog",
        probleem:
          "De mail-verzending zit in een eigen try/catch. Faalt de mail, dan krijgt de gebruiker tóch een 201, maar geen bevestiging. In klantbeleving: 'het lukte niet', terwijl de order wél is aangemaakt.",
        oplossing:
          "Mailfouten zichtbaar maken (loggen, retryen, alarmeren) en de gebruiker een duidelijke status geven i.p.v. een stille succesmelding.",
        gevolg:
          "Hoog risico — stille mailfouten ondermijnen het vertrouwen in boekingen en betalingen, en de klant denkt dat er niets gebeurd is.",
      },
      {
        titel: "From-alignment afdwingen",
        kort: "Vanaf noreply@ zodat Gmail niet dropt.",
        status: "gepland",
        risico: "middel",
        probleem:
          "Via het centrale ENV-transport dropt Gmail mail zonder From-alignment. Een verkeerde afzender betekent dat de mail nergens aankomt — ook niet in spam.",
        oplossing:
          "Versturen vanaf noreply@ met juiste SPF/DKIM-alignment, en custom SMTP per bedrijf waar dat nodig is.",
        gevolg:
          "Middel tot hoog risico — bevestigingsmails komen anders simpelweg niet aan, met dezelfde 'het lukte niet'-beleving als hierboven.",
      },
      {
        titel: "Webhook-retries & monitoring",
        kort: "Geen betaalde order op 'open'.",
        status: "idee",
        risico: "middel",
        probleem:
          "Mist de Mollie-webhook (tijdelijke fout, downtime), dan blijft een betaalde order op status 'open' staan. De betaling is binnen, maar het systeem weet het niet.",
        oplossing:
          "Retries plus monitoring/alarm op niet-verwerkte webhooks, en periodieke reconciliatie met Mollie als vangnet.",
        gevolg:
          "Middel risico — zeldzaam maar pijnlijk: de klant heeft betaald maar krijgt niets geleverd of bevestigd.",
      },
    ],
  },
];

const StatusPill = ({ status, isDark }) => {
  const s = ROADMAP_STATUS[status];
  if (!s) return null;
  return (
    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${isDark ? s.dark : s.light}`}>
      {s.label}
    </span>
  );
};

const DetailRow = ({ label, isDark, heading, muted, children }) => (
  <div>
    <p className={`mb-0.5 text-[11px] font-semibold uppercase tracking-wide ${heading}`}>{label}</p>
    <p className={`text-[12.5px] leading-relaxed ${muted}`}>{children}</p>
  </div>
);

const RISK_RANK = { hoog: 0, middel: 1, laag: 2 };
const RISK_DOT = {
  hoog: { dot: "bg-red-500", label: "Hoog" },
  middel: { dot: "bg-amber-500", label: "Middel" },
  laag: { dot: "bg-emerald-500", label: "Laag" },
};
const PRIORITY_SHORT = { must: "Must", should: "Should", could: "Nice" };

const RiskDot = ({ risico, muted }) => {
  const m = RISK_DOT[risico];
  if (!m) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[11.5px] ${muted}`}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
};

const PriorityText = ({ prioriteit, isDark }) => {
  const m = ROADMAP_PRIORITY[prioriteit];
  if (!m) return null;
  return (
    <span className={`whitespace-nowrap text-[11.5px] font-semibold ${isDark ? m.titleDark : m.titleLight}`}>
      {PRIORITY_SHORT[prioriteit]}
    </span>
  );
};

const LayerTable = ({ layer, items, isDark, heading, muted, active, onTag, open, toggle }) => {
  const lineB = isDark ? "border-white/[0.07]" : "border-gray-200";
  const hover = isDark ? "hover:bg-white/[0.03]" : "hover:bg-gray-50";
  const detailBg = isDark ? "bg-white/[0.02]" : "bg-gray-50/70";
  return (
    <div>
      {/* Laag-kop met scheidingslijn */}
      <div className="mb-2 flex items-center gap-3">
        <TagBadge
          layer={layer}
          isDark={isDark}
          active={active}
          onClick={onTag ? () => onTag(layer) : undefined}
        />
        <span className={`text-[11px] ${muted}`}>{items.length} punt{items.length === 1 ? "" : "en"}</span>
        <div className={`h-px flex-1 ${isDark ? "bg-white/10" : "bg-gray-200"}`} />
      </div>

      {/* Tabel — vanaf sm */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[540px] border-collapse text-left text-[12.5px]">
          <thead>
            <tr className={`border-b ${lineB}`}>
              <th className={`py-1.5 pr-3 text-[10.5px] font-semibold uppercase tracking-wide ${muted}`}>Verbetering</th>
              <th className={`w-[88px] px-2 py-1.5 text-[10.5px] font-semibold uppercase tracking-wide ${muted}`}>Prioriteit</th>
              <th className={`w-[92px] px-2 py-1.5 text-[10.5px] font-semibold uppercase tracking-wide ${muted}`}>Risico</th>
              <th className={`w-[88px] px-2 py-1.5 text-[10.5px] font-semibold uppercase tracking-wide ${muted}`}>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const isOpen = open.has(item.titel);
              return (
                <Fragment key={item.titel}>
                  <tr
                    onClick={() => toggle(item.titel)}
                    className={`cursor-pointer border-b ${lineB} ${hover}`}
                  >
                    <td className="py-2 pr-3 align-top">
                      <div className="flex items-start gap-1.5">
                        <span className={`mt-0.5 shrink-0 text-blue-500 transition ${isOpen ? "rotate-90" : ""}`}>▸</span>
                        <div className="min-w-0">
                          <span className={`font-semibold ${heading}`}>{item.titel}</span>
                          <span className={`block text-[11.5px] leading-snug ${muted}`}>{item.kort}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2 align-top"><PriorityText prioriteit={item.prioriteit} isDark={isDark} /></td>
                    <td className="px-2 py-2 align-top"><RiskDot risico={item.risico} muted={muted} /></td>
                    <td className="px-2 py-2 align-top"><StatusPill status={item.status} isDark={isDark} /></td>
                  </tr>
                  {isOpen && (
                    <tr className={`border-b ${lineB} ${detailBg}`}>
                      <td colSpan={4} className="px-2 py-3">
                        <div className="space-y-3 pl-5">
                          <DetailRow label="Probleem" isDark={isDark} heading={heading} muted={muted}>{item.probleem}</DetailRow>
                          <DetailRow label="Oplossing" isDark={isDark} heading={heading} muted={muted}>{item.oplossing}</DetailRow>
                          <DetailRow label="Risico als we niets doen" isDark={isDark} heading={heading} muted={muted}>{item.gevolg}</DetailRow>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Kaarten — onder sm */}
      <div className="space-y-2.5 sm:hidden">
        {items.map((item) => {
          const isOpen = open.has(item.titel);
          return (
            <div
              key={item.titel}
              className={`rounded-xl border ${lineB} ${isDark ? "bg-white/[0.02]" : "bg-white"}`}
            >
              <button
                type="button"
                onClick={() => toggle(item.titel)}
                className="flex w-full items-start gap-1.5 px-3.5 py-3 text-left"
              >
                <span className={`mt-0.5 shrink-0 text-blue-500 transition ${isOpen ? "rotate-90" : ""}`}>▸</span>
                <div className="min-w-0 flex-1">
                  <span className={`font-semibold ${heading}`}>{item.titel}</span>
                  <span className={`mt-0.5 block text-[11.5px] leading-snug ${muted}`}>{item.kort}</span>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <PriorityText prioriteit={item.prioriteit} isDark={isDark} />
                    <RiskDot risico={item.risico} muted={muted} />
                    <StatusPill status={item.status} isDark={isDark} />
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className={`space-y-3 border-t px-3.5 py-3 ${lineB} ${detailBg}`}>
                  <DetailRow label="Probleem" isDark={isDark} heading={heading} muted={muted}>{item.probleem}</DetailRow>
                  <DetailRow label="Oplossing" isDark={isDark} heading={heading} muted={muted}>{item.oplossing}</DetailRow>
                  <DetailRow label="Risico als we niets doen" isDark={isDark} heading={heading} muted={muted}>{item.gevolg}</DetailRow>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Roadmap = (p) => {
  const { isDark, text, muted, heading, activeLayers, onTag } = p;
  const [open, setOpen] = useState(() => new Set());
  const toggle = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const layers = LAYER_ORDER.filter(
    (l) => activeLayers.size === 0 || activeLayers.has(l),
  );
  return (
    <Section id="roadmap" icon={Sparkles} title="Wat we nog willen verbeteren" layers={LAYER_ORDER} showTags={false} {...p}>
      <P className={text}>
        Verbeterpunten <strong>per laag</strong> in een tabel, binnen elke laag
        gesorteerd <strong>op risico</strong>. Klik een rij aan voor probleem,
        oplossing en het gevolg als we het laten liggen. Filter bovenaan op een laag
        om de tabellen te beperken.
      </P>
      <div className="mb-1 mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[11.5px]">
        <span className={muted}>Prioriteit:</span>
        <PriorityText prioriteit="must" isDark={isDark} />
        <PriorityText prioriteit="should" isDark={isDark} />
        <PriorityText prioriteit="could" isDark={isDark} />
        <span className={`ml-2 ${muted}`}>Risico:</span>
        <RiskDot risico="hoog" muted={muted} />
        <RiskDot risico="middel" muted={muted} />
        <RiskDot risico="laag" muted={muted} />
        <span className={`ml-2 ${muted}`}>Status:</span>
        <StatusPill status="idee" isDark={isDark} />
        <StatusPill status="gepland" isDark={isDark} />
        <StatusPill status="bezig" isDark={isDark} />
      </div>

      <div className="mt-6 space-y-8">
        {layers.map((layer) => {
          const group = ROADMAP.find((g) => g.layer === layer);
          if (!group) return null;
          const items = group.items
            .map((item) => ({
              ...item,
              layer,
              prioriteit: item.prioriteit || RISK_TO_PRIORITY[item.risico],
            }))
            .sort((a, b) => RISK_RANK[a.risico] - RISK_RANK[b.risico]);
          return (
            <LayerTable
              key={layer}
              layer={layer}
              items={items}
              isDark={isDark}
              heading={heading}
              muted={muted}
              active={activeLayers.has(layer)}
              onTag={onTag}
              open={open}
              toggle={toggle}
            />
          );
        })}
      </div>

      <Callout tone="info" isDark={isDark} title="Mis je iets of wil je prioriteit verschuiven?">
        Prioriteit volgt standaard het risico (hoog → Must, middel → Should, laag →
        Nice), maar kan per item bewust anders. Heb je een wens of wil je iets
        op- of afschalen, geef het door — dan pas ik status, risico of prioriteit
        aan zodat het bord blijft kloppen.
      </Callout>
    </Section>
  );
};
